// should-open-on-click.ts — Project Bobcat, node property editor.
//
// Sibling of `should-toggle.ts`, and extracted for the same reason: the
// predicate was inline in `cell.tsx`'s `onClick`, where it cannot be tested
// without rendering the table.

/** The parts of a click event this decision needs. */
export interface ClickModifierState {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey?: boolean;
  /**
   * The click count (`MouseEvent.detail`). 2 or more means this `click` is the
   * SECOND (or later) of a double-click.
   */
  detail?: number;
}

/**
 * Whether a click should open the property editor.
 *
 * Only the modifier keys exclude a click, and all for the same reason: shift
 * builds a RANGE, and meta/ctrl TOGGLE a selection. Someone assembling a
 * multi-cell or multi-row selection is not asking to inspect one node, and
 * popping a 500px panel mid-gesture moves the grid under the pointer.
 *
 * `ctrlKey` is excluded as well as `metaKey` because the row-number handler
 * (`handleIndexCellClick`) branches on `e.metaKey || e.ctrlKey` — Ctrl+click is
 * the toggle gesture on Windows/Linux. Leaving it out meant a Windows user
 * building a multi-row selection opened the panel on the first Ctrl+click, and
 * then, once the selection spanned rows, was left with the open flag set and
 * nothing rendered.
 *
 * **The row-number column is NOT excluded.** It was, while the panel was
 * cell-scoped — a click there selects the whole row, and the columns it would
 * have shown (`order`, `id`) are read-only. Now that the panel is NODE-scoped,
 * a row selection is precisely the right input, and excluding it made column 0
 * the one place where clicking a row did nothing. Both call sites — `cell.tsx`
 * for a normal cell and `draggable-row.tsx`'s `handleIndexCellClick` for the
 * row number — share this predicate so the two gestures cannot drift.
 *
 * NOT excluded, deliberately: a click on the ALREADY-selected cell still
 * opens. The selection branches in `cell.tsx` early-return in that case
 * (`isSelected && selectedCells.length === 1`), which is why the call sits
 * before them.
 *
 * `detail >= 2` is excluded, and it has to be excluded HERE rather than left to
 * the store's grace timer alone. `click` fires once per press, so a
 * double-click delivers TWO of them: the first schedules the deferred open, the
 * second mousedown cancels it — and then the second `click` arrives and would
 * schedule it all over again, 250ms after the inline editor opened. The
 * mousedown cancel only holds if the click that follows it declines to
 * re-arm.
 */
export function shouldOpenPropertyEditorOnClick(
  e: ClickModifierState
): boolean {
  if ((e.detail ?? 0) >= 2) return false;

  return !e.shiftKey && !e.metaKey && !e.ctrlKey;
}
